import React, { useMemo } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Select,
  Input,
  Row,
  Col,
} from "antd";
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import useFormattedDate from "hooks/useFormatDate";
import { APP_STAGES } from "./ChargesConfigPage";

// badge styles
export const categoryColors = {
  FEE: { bg: "#e6f7ff", color: "#096dd9", border: "#91d5ff" },
  TAX: { bg: "#fff7e6", color: "#ad4e00", border: "#ffd591" },
  SURCHARGE: { bg: "#f9f0ff", color: "#722ed1", border: "#d3adf7" },
  DISCOUNT: { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
};

export const statusColors = {
  ACTIVE: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  INACTIVE: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

export const getTagStyle = (type, value) => {
  const colors =
    type === "category" ? categoryColors[value] : statusColors[value];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#000",
    padding: "1px 8px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: "Inter",
  };
};

// table + filters component
export default function ChargeConfigsTable({
  charges,
  loading,
  filters,
  setFilters,
  pagination,
  onPageChange,
  onEdit,
  onView,
  onToggleStatus,
}) {
  const { formatDateTime } = useFormattedDate();

  // apply filters
  const filteredCharges = useMemo(() => {
    return charges.filter((c) => {
      if (filters.stages?.length && !filters.stages.includes(c.stage))
        return false;
      if (filters.category && c.category !== filters.category) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.currency && c.currency !== filters.currency) return false;
      if (
        filters.code &&
        !c.code.toLowerCase().includes(filters.code.toLowerCase())
      )
        return false;
      return true;
    });
  }, [charges, filters]);

  // table columns
  const columns = [
    { title: "Code", dataIndex: "code", key: "code", ellipsis: true },
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => <Tag style={getTagStyle("category", cat)}>{cat}</Tag>,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (val, record) =>
        record.valueType === "PERCENTAGE"
          ? `${(val * 100).toFixed(2)}%`
          : `$${val.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag style={getTagStyle("status", status)}>{status}</Tag>
      ),
    },
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (val) => (
        <span style={{ fontSize: 12 }}>{formatDateTime(val)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onView(record);
              }}
            />
          </Tooltip>
          {/* <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
            />
          </Tooltip> */}
          <Tooltip
            title={record.status === "ACTIVE" ? "Deactivate" : "Activate"}
          >
            <Button
              size="small"
              icon={
                record.status === "ACTIVE" ? (
                  <CloseCircleOutlined />
                ) : (
                  <CheckCircleOutlined />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleStatus(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // filter bar
  const filterBar = (
    <Row gutter={[10, 5]} align="middle" style={{ marginBottom: 16 }}>
      <Col>
        <Select
          style={{ width: 350 }}
          value={filters.stages}
          mode="multiple"
          allowClear
          placeholder="Stages"
          options={APP_STAGES.map((s) => ({ value: s, label: s }))}
          onChange={(value) =>
            setFilters((prev) => ({ ...prev, stages: value }))
          }
          maxTagCount={1}
        />
      </Col>
      <Col>
        <Select
          style={{ width: 160 }}
          placeholder="Category"
          allowClear
          value={filters.category}
          options={[
            { value: "FEE", label: "Fee" },
            { value: "TAX", label: "Tax" },
            { value: "SURCHARGE", label: "Surcharge" },
            { value: "DISCOUNT", label: "Discount" },
          ]}
          onChange={(val) => setFilters((prev) => ({ ...prev, category: val }))}
        />
      </Col>
      <Col>
        <Select
          style={{ width: 140 }}
          placeholder="Status"
          allowClear
          value={filters.status}
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" },
          ]}
          onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
        />
      </Col>

      <Col>
        <Select
          style={{ width: 120 }}
          placeholder="Currency"
          allowClear
          value={filters.currency}
          options={[
            { value: "USD", label: "USD" },
            { value: "UGX", label: "UGX" },
          ]}
          onChange={(val) => setFilters((prev) => ({ ...prev, currency: val }))}
        />
      </Col>
      <Col>
        <Input
          style={{ width: 200, height: "100%" }}
          value={filters.code}
          placeholder="Search Code"
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, code: e.target.value }))
          }
        />
      </Col>
    </Row>
  );

  return (
    <>
      {filterBar}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredCharges}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
        }}
        onChange={(pag) => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          onPageChange({ current: pag.current, pageSize: pag.pageSize });
        }}
        onRow={(record) => ({
          onClick: () => onView(record),
          style: { cursor: "pointer" },
        })}
        scroll={{ x: 1200 }}
        size="small"
        className="custom-table"
      />
    </>
  );
}
